import React, { useCallback, useState } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Backdrop,
  CircularProgress,
  Typography,
} from '@mui/material';
import { ErrorBanner } from '@cosmotech/ui';
import { TECHNICAL_POLICY_COLUMNS } from '../../../../services/technicalPolicy/technicalPolicyColumns';
import EditableTable from '../../components/EditableTable';
import { useNiveauDePriorisationTab } from '../tabs/hooks/NiveauDePriorisationHook';

const labels = {
  niveauDePriorisation: 'genericcomponent.technicalPolicy.prioritizationDialog.title',
  sauvegarder: 'genericcomponent.common.save',
  annuler: 'genericcomponent.common.cancel',
  fermer: 'genericcomponent.common.close',
  suivant: 'genericcomponent.common.next',
};

export const NiveauPriorisationDialog = (props) => {
  const { t } = useTranslation();
  const { editMode, open, setOpen, saveData } = props;
  const [saveSchedulingLevelError, setSaveSchedulingLevelError] = useState(null);
  const { sortedNiveauDePriorisation, status } = useNiveauDePriorisationTab();
  const methods = useForm({ defaultValues: {}, mode: 'onChange' });
  const { dirtyFields } = methods.formState;
  const reset = methods.reset;
  const isDirty = Object.keys(dirtyFields ?? {}).length !== 0;

  const save = useCallback(() => {
    const values = methods.getValues();
    const reset = methods.reset;
    saveData(values, reset, setSaveSchedulingLevelError);
  }, [methods, saveData, setSaveSchedulingLevelError]);

  const handleDiscardChanges = useCallback(() => {
    reset({});
    setOpen(false);
  }, [reset, setOpen]);

  const clearSaveSchedulingLevelError = () => {
    setSaveSchedulingLevelError(null);
  };

  const columns = TECHNICAL_POLICY_COLUMNS.NiveauDePriorisation();
  const confirmDeleteDialogLabels = {
    id: 'delete-scheduling-level',
    labels: {
      title: t(
        'genericcomponent.common.confirmDialogs.deletePrioritizationLevel.title',
        'Delete prioritization level?'
      ),
      body: t(
        'genericcomponent.common.confirmDialogs.deletePrioritizationLevel.body',
        'The prioritization level could be used in a management rule or an imposed actions sequence'
      ),
      button1: t('genericcomponent.common.cancel', 'CANCEL'),
      button2: t('genericcomponent.common.delete', 'DELETE'),
    },
  };

  return (
    <Dialog open={open} fullWidth maxWidth="lg">
      <ErrorBanner
        error={saveSchedulingLevelError}
        clearErrors={clearSaveSchedulingLevelError}
        labels={{
          dismissButtonText: t('commoncomponents.banner.button.dismiss', 'Dismiss'),
          tooLongErrorMessage: t(
            'commoncomponents.banner.tooLongErrorMessage',
            'Detailed error message is too long to be displayed. ' +
              'To read it, please use the COPY button and paste it in your favorite text editor.'
          ),
          secondButtonText: t('commoncomponents.banner.button.copy.label', 'Copy'),
          toggledButtonText: t('commoncomponents.banner.button.copy.copied', 'Copied'),
        }}
      />
      <DialogTitle>{t(labels.niveauDePriorisation)}</DialogTitle>
      <DialogContent>
        <Box width={'100%'}>
          <FormProvider {...methods} key={`form-technical-policy-wizard-niveau-priorisation`}>
            <Controller
              name="NiveauDePriorisation"
              defaultValue={sortedNiveauDePriorisation ?? []}
              render={({ field }) => {
                const { value, onChange } = field;
                return (
                  <EditableTable
                    id="NiveauDePriorisation"
                    columns={columns.filter((col) => col.isInvisible !== true)}
                    rows={value ?? []}
                    onChange={onChange}
                    editMode={editMode}
                    confirmDeleteRow={true}
                    confirmDeleteRowDialogProps={confirmDeleteDialogLabels}
                  />
                );
              }}
            />
          </FormProvider>
        </Box>
      </DialogContent>
      <DialogActions>
        <Box sx={{ width: 1, display: 'flex', justifyContent: 'space-between', gap: 2, m: 2 }}>
          <Button
            data-cy="close-niveau-priorisation-dialog-button"
            onClick={handleDiscardChanges}
            variant="outlined"
            color="secondary"
          >
            {isDirty ? t(labels.annuler) : t(labels.fermer)}
          </Button>
          {editMode && (
            <Button
              data-cy="save-technical-priorisation-button"
              disabled={!isDirty}
              onClick={save}
              variant="contained"
              color="primary"
            >
              {t(labels.sauvegarder)}
            </Button>
          )}
        </Box>
      </DialogActions>
      <Backdrop open={status === 'SAVING'}>
        <CircularProgress data-cy="saving-niveau-priorisation-spinner" color="inherit" />
        <Typography variant="h4">{labels.sauvegardeEnCours}</Typography>
      </Backdrop>
    </Dialog>
  );
};

NiveauPriorisationDialog.propTypes = {
  editMode: PropTypes.bool,
  open: PropTypes.bool,
  setOpen: PropTypes.func.isRequired,
  saveData: PropTypes.func.isRequired,
};
